import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiX, FiUser, FiMail, FiPhone, FiBriefcase, FiMapPin, FiFileText, FiGlobe, FiCheck } from "react-icons/fi";
import CustomSelect from "./CustomSelect";
import { toast } from "sonner";
import { updateDealerProfile, closeEditProfileModal } from "../redux/dealerSlice";
import { updateUser } from "../redux/authSlice";
import type { RootState, AppDispatch } from "../redux/store";
import type { DealerProfile } from "../types/dealer.types";

const THEME = "#B08D55";

const STATES = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", "Florida", "Georgia",
    "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland",
    "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
    "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania",
    "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
    "West Virginia", "Wisconsin", "Wyoming",
];

export default function EditProfileModal() {
    const dispatch = useDispatch<AppDispatch>();
    const { profile, isEditProfileModalOpen, loading } = useSelector((state: RootState) => state.dealer);

    const [formData, setFormData] = useState<Partial<DealerProfile>>({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        companyName: "",
        address: "",
        city: "",
        state: "",
        about: "",
        businessWebsite: "",
        isResidential: false,
    });

    useEffect(() => {
        if (profile) {
            setFormData({
                firstName: profile.firstName || "",
                lastName: profile.lastName || "",
                email: profile.email || "",
                phone: profile.phone || "",
                companyName: profile.companyName || "",
                address: profile.address || "",
                city: profile.city || "",
                state: profile.state || "",
                about: profile.about || "",
                businessWebsite: profile.businessWebsite || "",
                isResidential: profile.isResidential ?? false,
            });
        }
    }, [profile]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        try {
            const result = await dispatch(updateDealerProfile(formData)).unwrap();
            // Sync with auth state for sidebar & localStorage
            if (result) {
                dispatch(updateUser({
                    firstName: result.firstName,
                    lastName: result.lastName,
                    companyName: result.companyName,
                    email: result.email,
                }));
            }

            dispatch(closeEditProfileModal());
            toast.success("Profile updated successfully");
        } catch (error: any) {
            console.error("Update failed:", error);
            // Error handling is managed by the slice state usually, but adding toast here is safe
            toast.error(typeof error === 'string' ? error : "Failed to update profile");
        }
    };

    if (!isEditProfileModalOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col animate-fade-in-up">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                    <h2 className="text-xl font-bold text-gray-800">Edit Profile</h2>
                    <button
                        onClick={() => dispatch(closeEditProfileModal())}
                        className="p-2 hover:bg-gray-100 rounded-full text-gray-500 transition"
                    >
                        <FiX size={24} />
                    </button>
                </div>

                {/* Body - Scrollable */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="space-y-6">
                        {/* Personal Details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="group">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">First Name</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                        <FiUser />
                                    </div>
                                    <input
                                        type="text"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:border-[#B08D55] focus:ring-4 focus:ring-[#B08D55]/10 transition-all outline-none text-gray-700"
                                        placeholder="John"
                                    />
                                </div>
                            </div>
                            <div className="group">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Last Name</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                        <FiUser />
                                    </div>
                                    <input
                                        type="text"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:border-[#B08D55] focus:ring-4 focus:ring-[#B08D55]/10 transition-all outline-none text-gray-700"
                                        placeholder="Doe"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Contact Details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="group">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Email Address</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                        <FiMail />
                                    </div>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:border-[#B08D55] focus:ring-4 focus:ring-[#B08D55]/10 transition-all outline-none text-gray-700"
                                        placeholder="john@example.com"
                                    />
                                </div>
                            </div>
                            <div className="group">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Phone Number</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                        <FiPhone />
                                    </div>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:border-[#B08D55] focus:ring-4 focus:ring-[#B08D55]/10 transition-all outline-none text-gray-700"
                                        placeholder="+1 (555) 000-0000"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Company Details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="group">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Company Name</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                        <FiBriefcase />
                                    </div>
                                    <input
                                        type="text"
                                        name="companyName"
                                        value={formData.companyName}
                                        onChange={handleChange}
                                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:border-[#B08D55] focus:ring-4 focus:ring-[#B08D55]/10 transition-all outline-none text-gray-700 font-medium"
                                        placeholder="My Audio Visual Solutions"
                                    />
                                </div>
                            </div>
                            <div className="group">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Business Website</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                        <FiGlobe />
                                    </div>
                                    <input
                                        type="url"
                                        name="businessWebsite"
                                        value={formData.businessWebsite}
                                        onChange={handleChange}
                                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:border-[#B08D55] focus:ring-4 focus:ring-[#B08D55]/10 transition-all outline-none text-gray-700"
                                        placeholder="https://example.com"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Address Section */}
                        <div className="pt-4 border-t border-gray-100">
                            {/* Heading Area */}
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-3">
                                <h4 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                                    <FiMapPin className="text-[#B08D55]" /> Location Details
                                </h4>
                                <p className="text-[11px] text-gray-400">
                                    Choose how your location displays on the dealer locator
                                </p>
                            </div>

                            {/* Address Type Options (Stacked Below Heading in 2-Column Grid) */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                                {/* Residential Show House */}
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, isResidential: true }))}
                                    className={`p-3 rounded-xl border-2 transition-all flex items-center justify-between gap-3 cursor-pointer text-left ${
                                        formData.isResidential
                                            ? "border-[#B08D55] bg-[#B08D55]/5 shadow-xs"
                                            : "border-gray-200 bg-gray-50/70 hover:bg-white hover:border-gray-300"
                                    }`}
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div
                                            className={`w-7 h-7 shrink-0 transition-colors ${
                                                formData.isResidential ? "bg-[#B08D55]" : "bg-gray-400"
                                            }`}
                                            style={{
                                                WebkitMaskImage: 'url(/house.png)',
                                                WebkitMaskSize: 'contain',
                                                WebkitMaskRepeat: 'no-repeat',
                                                WebkitMaskPosition: 'center',
                                                maskImage: 'url(/house.png)',
                                                maskSize: 'contain',
                                                maskRepeat: 'no-repeat',
                                                maskPosition: 'center',
                                            }}
                                        />
                                        <div className="min-w-0">
                                            <p className={`text-xs font-bold uppercase tracking-wider ${
                                                formData.isResidential ? "text-gray-900" : "text-gray-600"
                                            }`}>
                                                Residential Show House
                                            </p>
                                            <p className="text-[11px] text-gray-500 truncate">
                                                Private residence or showroom
                                            </p>
                                        </div>
                                    </div>

                                    <div
                                        className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
                                            formData.isResidential
                                                ? "border-[#B08D55] bg-[#B08D55] text-white"
                                                : "border-gray-300 bg-white"
                                        }`}
                                    >
                                        {formData.isResidential && <FiCheck size={11} strokeWidth={3} />}
                                    </div>
                                </button>

                                {/* Store */}
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, isResidential: false }))}
                                    className={`p-3 rounded-xl border-2 transition-all flex items-center justify-between gap-3 cursor-pointer text-left ${
                                        !formData.isResidential
                                            ? "border-[#B08D55] bg-[#B08D55]/5 shadow-xs"
                                            : "border-gray-200 bg-gray-50/70 hover:bg-white hover:border-gray-300"
                                    }`}
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div
                                            className={`w-7 h-7 shrink-0 transition-colors ${
                                                !formData.isResidential ? "bg-[#B08D55]" : "bg-gray-400"
                                            }`}
                                            style={{
                                                WebkitMaskImage: 'url(/store.png)',
                                                WebkitMaskSize: 'contain',
                                                WebkitMaskRepeat: 'no-repeat',
                                                WebkitMaskPosition: 'center',
                                                maskImage: 'url(/store.png)',
                                                maskSize: 'contain',
                                                maskRepeat: 'no-repeat',
                                                maskPosition: 'center',
                                            }}
                                        />
                                        <div className="min-w-0">
                                            <p className={`text-xs font-bold uppercase tracking-wider ${
                                                !formData.isResidential ? "text-gray-900" : "text-gray-600"
                                            }`}>
                                                Store
                                            </p>
                                            <p className="text-[11px] text-gray-500 truncate">
                                                Commercial retail shopfront
                                            </p>
                                        </div>
                                    </div>

                                    <div
                                        className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
                                            !formData.isResidential
                                                ? "border-[#B08D55] bg-[#B08D55] text-white"
                                                : "border-gray-300 bg-white"
                                        }`}
                                    >
                                        {!formData.isResidential && <FiCheck size={11} strokeWidth={3} />}
                                    </div>
                                </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="md:col-span-2 group">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Street Address</label>
                                    <input
                                        type="text"
                                        name="address"
                                        value={formData.address}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:border-[#B08D55] focus:ring-4 focus:ring-[#B08D55]/10 transition-all outline-none text-gray-700"
                                        placeholder="123 Main St, Suite 100"
                                    />
                                </div>
                                <div className="group">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">City</label>
                                    <input
                                        type="text"
                                        name="city"
                                        value={formData.city}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:border-[#B08D55] focus:ring-4 focus:ring-[#B08D55]/10 transition-all outline-none text-gray-700"
                                        placeholder="New York"
                                    />
                                </div>
                                <div className="group">
                                    <CustomSelect
                                        label="State / Province"
                                        name="state"
                                        value={formData.state || ""}
                                        options={[
                                            ...(formData.state && !STATES.includes(formData.state)
                                                ? [{ value: formData.state, label: formData.state }]
                                                : []),
                                            ...STATES.map(s => ({ value: s, label: s }))
                                        ]}
                                        onChange={(name, value) => setFormData(prev => ({ ...prev, [name]: value }))}
                                        placeholder="Select State"
                                        className="h-[46px]" // Match height roughly with other inputs
                                    />
                                </div>
                            </div>
                        </div>

                        {/* About Section */}
                        <div className="grid grid-cols-1 gap-6">
                            <div className="group">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block flex items-center gap-2">
                                    About Dealer
                                </label>
                                <p className="fmtv-helper-text">This will be visible on your public store locator profile.</p>
                                <div className="relative">
                                    <div className="absolute top-3 left-3 pointer-events-none text-gray-400">
                                        <FiFileText />
                                    </div>
                                    <textarea
                                        name="about"
                                        value={formData.about}
                                        onChange={handleChange}
                                        rows={4}
                                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:border-[#B08D55] focus:ring-4 focus:ring-[#B08D55]/10 transition-all outline-none text-gray-700 resize-none"
                                        placeholder="Tell customers about your business history..."
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 rounded-b-2xl">
                    <button
                        onClick={() => dispatch(closeEditProfileModal())}
                        className="px-6 py-2.5 text-gray-600 font-semibold hover:bg-gray-200 rounded-lg transition"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={loading}
                        className="px-6 py-2.5 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition disabled:opacity-50"
                        style={{ backgroundColor: THEME }}
                    >
                        {loading ? "Saving..." : "Save Changes"}
                    </button>
                </div>
            </div>
        </div>
    );
}
